import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';

import { Media } from '@eiq/files/models';
import { GetNextId } from '@eiq/shared/utils';

import {
  BlockType,
  BlockLoopCustomOption,
  BlockQuotaModel,
  Command,
  Condition,
  LoopAllocationSettings,
  LoopSourceMedia,
  BlockLoopSourceEnum
} from '@empathy/survey/base';

import { IBlockForm } from './block-form.interface';
import { IBlockLoopCustomOptionForm } from './block-loop-custom-option-form.interface';
import { BlockLoopCustomOptionForm } from './block-loop-custom-option-form.model';

import { BlockQuotaForm } from './block-quota-form.model';
import { LoopAuthoringBlock } from './loop-authoring-block.interface';
import { LoopMediaCatalogue } from './loop-media-catalogue.model';
import { ConditionForm } from '../conditions/condition-form.model';

export class BlockForm {
  form = new FormGroup<IBlockForm>({
    id: new FormControl<string>(''),
    title: new FormControl<string>(''),
    description: new FormControl<string>(''),
    type: new FormControl<BlockType>(null),
    sourceQId: new FormControl<string>(''),
    groupedQuestionIds: new FormArray([]),
    visibleWhen: new FormArray([]),
    randomizedInd: new FormControl<boolean>(false),
    groupedBlockIds: new FormArray([]),
    isSubBlock: new FormControl<boolean>(false),
    quotas: new FormArray<FormGroup<BlockQuotaForm>>([]),
    evenDistributionInd: new FormControl<boolean>(false),
    evenDistributionQuantity: new FormControl<number>(1, [Validators.min(1)]),
    sourceType: new FormControl<BlockLoopSourceEnum>(BlockLoopSourceEnum.Question),
    lastSourceIndex: new FormControl<number>(0, { nonNullable: true }),
    lastOptionIndex: new FormControl<number>(0, { nonNullable: true }),
    sourceOptions: new FormArray<FormGroup<IBlockLoopCustomOptionForm>>([]),
    sourceOptionsRandomizedInd: new FormControl<boolean>(false),
    allocation: new FormControl<LoopAllocationSettings>({ value: undefined, disabled: false }, { nonNullable: true }),
    sourceMedia: new FormControl<LoopSourceMedia[]>({ value: undefined, disabled: false }, { nonNullable: true }),
    stimulusQuestionIds: new FormControl<string[]>({ value: undefined, disabled: false }, { nonNullable: true })
  });

  private quotaSubscriptions: Subscription[] = [];

  constructor(blockId: string, blockType: BlockType, isSubBlock: boolean = false) {
    this.form.get('id').setValue(blockId);
    this.form.get('type').setValue(blockType);
    this.form.get('isSubBlock').setValue(isSubBlock);
    if (blockType === BlockType.LoopParent) {
      this.form.controls.sourceType.setValue(undefined);
      this.form.controls.lastSourceIndex.setValue(undefined);
      this.form.controls.lastOptionIndex.setValue(undefined);
      this.form.controls.evenDistributionQuantity.setValue(undefined);
    }
  }

  patch(block: LoopAuthoringBlock): void {
    this.quotaSubscriptions.forEach((s) => s.unsubscribe());
    this.quotaSubscriptions = [];
    this.form.patchValue(block);
    if (block.type !== BlockType.LoopParent) {
      // Older custom loops stored their option counter in lastSourceIndex.
      const legacyOptions = block.lastOptionIndex === undefined && block.sourceType === BlockLoopSourceEnum.Options;
      const lastExistingOptionIndex = Number(GetNextId(block.sourceOptions ?? [], 'opt').value) - 1;
      const legacyOptionIndex = legacyOptions ? (block.lastSourceIndex ?? 0) : 0;
      this.form.controls.lastOptionIndex.setValue(Math.max(block.lastOptionIndex ?? legacyOptionIndex, lastExistingOptionIndex));
      this.form.controls.lastSourceIndex.setValue(
        (block.sourceMedia ?? []).reduce(
          (last, media) => Math.max(last, media.sourceIndex),
          legacyOptions && !block.sourceMedia?.length ? 0 : (block.lastSourceIndex ?? 0)
        )
      );
    }
    this.patchGroupedQuestions(block.groupedQuestionIds);
    this.patchGroupedBlockIds(block.groupedBlockIds);
    this.patchQuotas(block.quotas);
    this.patchVisibleWhen(block.visibleWhen);
    this.patchSourceOptions(block.sourceOptions);
  }

  addSourceMedia(media: Media): void {
    new LoopMediaCatalogue(this.form).add(media);
  }

  private patchGroupedQuestions(groupedQuestionIds: string[]): void {
    if (!groupedQuestionIds || groupedQuestionIds.length === 0) {
      return;
    }
    groupedQuestionIds.forEach((qId) => {
      (this.form.get('groupedQuestionIds') as FormArray).push(new FormControl(qId));
    });
  }

  private patchGroupedBlockIds(groupedBlockIds: string[]): void {
    if (!groupedBlockIds || groupedBlockIds.length === 0) {
      return;
    }
    groupedBlockIds.forEach((bId) => {
      (this.form.get('groupedBlockIds') as FormArray).push(new FormControl(bId));
    });
  }

  private patchSourceOptions(sourceOptions: BlockLoopCustomOption[]): void {
    if (!sourceOptions || sourceOptions.length === 0) {
      return;
    }

    sourceOptions.forEach((bId) => {
      (this.form.get('sourceOptions') as FormArray).push(new BlockLoopCustomOptionForm(bId).form);
    });
  }

  private patchQuotas(quotas: BlockQuotaModel[]): void {
    if (!quotas || quotas.length === 0) {
      return;
    }
    quotas.forEach((quota) => {
      const quotaControl = new FormControl<number>(quota.quota, [Validators.min(1)]);

      const subscription = quotaControl.valueChanges.subscribe((value) => {
        if (value < 1) {
          quotaControl.setValue(1, { emitEvent: false });
        }
      });

      (this.form.get('quotas') as FormArray).push(
        new FormGroup({
          id: new FormControl(quota.id),
          quota: quotaControl
        })
      );

      this.quotaSubscriptions.push(subscription);
    });
  }

  private patchVisibleWhen(visibleWhen: (Condition | Command)[]): void {
    if (!visibleWhen || visibleWhen.length === 0) {
      return;
    }

    visibleWhen.forEach((condition) => {
      if (typeof condition === 'object') {
        (this.form.get('visibleWhen') as FormArray).push(new ConditionForm(condition as Condition).form);
      } else {
        (this.form.get('visibleWhen') as FormArray).push(new FormControl(condition as Command));
      }
    });
  }
}
